'use client';

interface HeroProps {
  phone?: string;
  quoteHref?: string;
}

export default function Hero({
  phone = "03 8456 9966",
  quoteHref = "#quote",
}: HeroProps) {
  const phoneFormatted = phone.replace(/\s+/g, '');
  const phoneTel = `+61${phoneFormatted.substring(1)}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "VALCO GROUP Financial Services — Chattel Mortgage Australia",
    description: "Chattel mortgage and car finance specialists for Australian businesses. Same-day approvals, 60+ lenders, flexible terms up to 7 years.",
    url: "https://vgfs.com.au/chattel-mortgage",
    areaServed: {
      "@type": "Country",
      "name": "Australia"
    },
    serviceType: [
      "Chattel Mortgage",
      "Business Car Finance",
      "Self-Employed Car Finance",
      "Low-Doc Vehicle Finance",
      "ABN Car Loans"
    ],
    telephone: phone,
    priceRange: "$$",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "180"
    },
    sameAs: [
      "https://vgfs.com.au",
      "https://g.page/r/CUhNqwdJIBASEBI"
    ],
  };

  const usps = [
    "Terms up to 7 years • Balloons up to 40%",
    "Finance from $10k to $2M+ • New & Used (no age limits)",
    "Low-doc friendly • Up to $250k without financials",
    "Same-day pre-approvals. Settlement typically within 24 to 48 hours",
  ];

  return (
    <header className="relative isolate z-[1]" style={{ backgroundColor: '#0F2A44', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12 text-center">
        {/* Trust badge */}
        <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
          <span className="text-orange-400 text-sm font-semibold">⭐⭐⭐⭐⭐ 5.0 Google Rating</span>
          <span className="text-slate-400 text-sm">|</span>
          <span className="text-slate-300 text-sm">180+ Verified Reviews</span>
        </div>

        {/* Main headline - H1 for SEO */}
        <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.1]">
          <span className="text-orange-500">Chattel Mortgage</span> & Car Finance For Australian Businesses
        </h1>

        {/* Subheadline */}
        <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Same-day pre-approvals with expert broker advice. Access 50+ lenders and flexible finance solutions built for cash flow, tax deductions and business growth.
        </p>

        {/* Key trust bullets */}
        <div className="mt-10 max-w-2xl mx-auto">
          <ul className="space-y-4 text-base sm:text-lg text-slate-200 text-left" aria-label="Key benefits">
            {usps.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <svg 
                  aria-hidden="true" 
                  viewBox="0 0 24 24" 
                  className="h-6 w-6 flex-none text-orange-500 mt-0.5" 
                  fill="currentColor"
                >
                  <path d="M9.55 17.54 4.8 12.8l1.4-1.4 3.35 3.35 8.25-8.25 1.4 1.4-9.65 9.64Z" />
                </svg>
                <span className="flex-1">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href={quoteHref}
            className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-8 py-4 text-white text-lg font-bold shadow-lg shadow-orange-500/25 hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 transition-all hover:-translate-y-0.5 w-full sm:w-auto"
            data-action="quote_click"
            data-testid="button-get-quote-hero"
          >
            Get Your Free Quote
            <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
          <a
            href={`tel:${phoneTel}`}
            className="inline-flex items-center justify-center rounded-xl border-2 border-slate-500 px-8 py-4 text-white text-lg font-semibold hover:bg-white/10 hover:border-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40 transition-all w-full sm:w-auto"
            data-action="click_call"
            data-testid="button-call-expert-hero"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call {phone}
          </a>
        </div>

        {/* Trust strip */}
        <div className="mt-10 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-slate-400">
          <span className="flex items-center gap-1">
            <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            50+ Lenders
          </span>
          <span className="flex items-center gap-1">
            <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            No Upfront Fees
          </span>
          <span className="flex items-center gap-1">
            <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Australian Brokers
          </span>
          <span className="flex items-center gap-1">
            <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Fast Settlement
          </span>
        </div>
      </div>
    </header>
  );
}